import { Check, Copy, Download, EyeOff, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { SnailGlyph } from '@/components/ui/SnailGlyph';
import { cn } from '@/lib/utils';

import { usePositionForCoin } from '../hooks/useLivePosition';
import { usePnlCardExport } from '../hooks/usePnlCardExport';
import { pnlCardFromPosition, type PnlCardData } from '../utils/pnlCardData';

import { CARD_H, CARD_W, PnlShareCard } from './PnlShareCard';

/**
 * "Share your PnL" dialog.
 *
 * Opened either with a finished card (a closed trade already carries every
 * figure) or with just a coin, in which case the live position for that coin
 * is read here. Sharing the market the trading UI is already polling is a
 * cache hit on the same query key, so the common path never shows a spinner.
 */

export type PnlShareRequest =
    /** Every figure is already known (a closed trade, or a demo position). */
    | { kind: 'ready'; data: PnlCardData }
    /** A real open position — read the authoritative position for this coin. */
    | { kind: 'position'; coin: string };

function usePreviewScale(): [React.RefObject<HTMLDivElement | null>, number] {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.5);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () =>
            setScale(Math.min(1, (el.clientWidth || CARD_W) / CARD_W));
        measure();
        // jsdom has no ResizeObserver; the single measure above suffices there.
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return [ref, scale];
}

/**
 * Mounted only once there is a card to export — the hook needs a live node,
 * and it renders outside the preview's scale transform so the buttons stay
 * full size however small the card preview gets.
 */
const PnlShareActions: React.FC<{
    cardRef: React.RefObject<HTMLDivElement | null>;
    data: PnlCardData;
    hideBalances: boolean;
    onToggleBalances: () => void;
}> = ({ cardRef, data, hideBalances, onToggleBalances }) => {
    const { state, action, download, copy, canCopy } = usePnlCardExport(
        cardRef,
        data,
    );

    const busy = state === 'busy';
    const label = (which: string, idle: string, done: string) => {
        if (action !== which) return idle;
        if (state === 'busy') return 'Rendering…';
        if (state === 'done') return done;
        if (state === 'error') return 'Failed, retry';
        return idle;
    };
    const settled = (which: string) => action === which && state === 'done';

    return (
        <div className="pnl-share__actions">
            <button
                type="button"
                className={cn(
                    'pnl-share__btn',
                    hideBalances && 'pnl-share__btn--on',
                )}
                aria-pressed={hideBalances}
                onClick={onToggleBalances}
            >
                <EyeOff size={15} />
                Hide balances
            </button>
            <span className="pnl-share__spacer" />
            {canCopy && (
                <button
                    type="button"
                    className="pnl-share__btn"
                    disabled={busy}
                    onClick={copy}
                >
                    {settled('copy') ? <Check size={15} /> : <Copy size={15} />}
                    {label('copy', 'Copy image', 'Copied')}
                </button>
            )}
            <button
                type="button"
                className="pnl-share__btn pnl-share__btn--primary"
                disabled={busy}
                onClick={download}
            >
                {settled('download') ? (
                    <Check size={15} />
                ) : (
                    <Download size={15} />
                )}
                {label('download', 'Download', 'Saved')}
            </button>
        </div>
    );
};

const PnlShareDialog: React.FC<{
    request: PnlShareRequest;
    onDismiss: () => void;
    /** Heading override — the live tape shares other people's trades, not "your" PnL. */
    title?: string;
    subtitle?: string;
}> = ({ request, onDismiss, title = 'Share your PnL', subtitle }) => {
    const isPosition = request.kind === 'position';
    const coin = isPosition ? request.coin : null;

    const position = usePositionForCoin(coin, isPosition);

    // No price-feed input on purpose: the card's mark is recovered from the
    // position so it reconciles with the PnL printed beside it. The feed carries
    // LAST price, which is a different number with a different job (it draws the
    // chart) — see pnlCardFromPosition.

    const data: PnlCardData | null = isPosition
        ? position.hasPosition
            ? pnlCardFromPosition(request.coin, position)
            : null
        : request.data;

    const [hideBalances, setHideBalances] = useState(false);
    const [stageRef, scale] = usePreviewScale();
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onDismiss();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onDismiss]);

    // Portalled to <body>: the mobile history lives inside a Vaul drawer whose
    // `transform` would otherwise become the containing block for this fixed
    // overlay and trap it inside the sheet.
    return createPortal(
        <div
            className="pnl-share"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            // Matches the open-overlay probe in useKeyboardShortcuts, so the
            // global L/S/Esc trading shortcuts stand down while this is up.
            data-state="open"
            onClick={onDismiss}
        >
            <div
                className="pnl-share__shell"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="pnl-share__head">
                    <div>
                        <h2 className="pnl-share__title">{title}</h2>
                        <p className="pnl-share__sub">
                            {subtitle && data
                                ? subtitle
                                : data
                                    ? `Snapshot your ${data.coin} trade and take it with you.`
                                    : 'Reading your position…'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pnl-share__close"
                        aria-label="Close"
                        onClick={onDismiss}
                    >
                        <X size={17} />
                    </button>
                </header>

                <div
                    className="pnl-share__stage"
                    ref={stageRef}
                    style={{ height: CARD_H * scale }}
                >
                    {data ? (
                        <div
                            style={{
                                transform: `scale(${scale})`,
                                transformOrigin: 'top left',
                            }}
                        >
                            <PnlShareCard
                                ref={cardRef}
                                data={data}
                                hideBalances={hideBalances}
                            />
                        </div>
                    ) : (
                        <div className="pnl-share__empty">
                            <SnailGlyph
                                size={30}
                                spinning={position.isLoading}
                            />
                            <span>
                                {position.isError
                                    ? 'That position could not be read.'
                                    : position.isLoading
                                        ? 'Reading your position…'
                                        : 'This position is no longer open.'}
                            </span>
                        </div>
                    )}
                </div>

                {data && (
                    <PnlShareActions
                        cardRef={cardRef}
                        data={data}
                        hideBalances={hideBalances}
                        onToggleBalances={() => setHideBalances(!hideBalances)}
                    />
                )}
            </div>

            <div className="pnl-share__exit">
                <span className="pnl-share__flag" />
                Click anywhere to exit
                <span className="pnl-share__flag" />
            </div>
        </div>,
        document.body,
    );
};

export default PnlShareDialog;
